import React, { Component } from "react";
import * as Icon from "react-feather";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import GmailService from "../../services/GmailService";
import RecipientField from "./RecipientField";

const QUILL_MODULES = {
	toolbar: [
		[{ header: [1, 2, 3, false] }],
		["bold", "italic", "underline", "strike"],
		[{ list: "ordered" }, { list: "bullet" }],
		["link", "blockquote", "code-block"],
		[{ color: [] }, { background: [] }],
		["clean"],
	],
	clipboard: { matchVisual: false },
};

const QUILL_FORMATS = [
	"header",
	"bold",
	"italic",
	"underline",
	"strike",
	"list",
	"link",
	"blockquote",
	"code-block",
	"color",
	"background",
];

function formatBytes(n) {
	if (!n) return "";
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function splitEmail(headerValue) {
	if (!headerValue) return { name: "", email: "" };
	const m = headerValue.match(/^\s*"?([^"<]+?)"?\s*<(.+?)>\s*$/);
	if (m) return { name: m[1].trim(), email: m[2].trim() };
	return { name: headerValue.trim(), email: headerValue.trim() };
}

function buildQuotedBlock(originalMessage) {
	if (!originalMessage) return "";
	const dateLine = originalMessage.date || "";
	const fromLine = originalMessage.from || "";
	const body = originalMessage.bodyHtml || (originalMessage.bodyText || "").replace(/\n/g, "<br>");
	const intro = `On ${dateLine}, ${fromLine} wrote:`;
	return `<br><br><div class="gmail_quote"><div>${intro}</div><blockquote style="margin:0 0 0 .8ex;border-left:1px solid #ccc;padding-left:1ex">${body}</blockquote></div>`;
}

function buildForwardBlock(originalMessage) {
	if (!originalMessage) return "";
	return `<br><br><div class="gmail_quote"><div>---------- Forwarded message ---------</div>
<div>From: ${originalMessage.from || ""}</div>
<div>Date: ${originalMessage.date || ""}</div>
<div>Subject: ${originalMessage.subject || ""}</div>
<div>To: ${originalMessage.to || ""}</div>
${originalMessage.cc ? `<div>Cc: ${originalMessage.cc}</div>` : ""}
<br>
${originalMessage.bodyHtml || (originalMessage.bodyText || "").replace(/\n/g, "<br>")}
</div>`;
}

const AUTOSAVE_DELAY_MS = 2000;

class ComposeModal extends Component {
	constructor(props) {
		super(props);

		const initial = this.computeInitialState(props);
		this.state = {
			...initial,
			files: [],
			sending: false,
			saving: false,
			savedAt: null,
			error: null,
			showCc: !!(initial.cc && initial.cc.length),
			showBcc: !!(initial.bcc && initial.bcc.length),
			draftId: props.prefill && props.prefill.draftId ? props.prefill.draftId : null,
		};
		this.autosaveTimer = null;
		this.fileInputRef = React.createRef();
	}

	computeInitialState(props) {
		const prefill = props.prefill || {};
		const mode = prefill.mode || "new";
		const source = prefill.sourceMessage || null;

		let to = prefill.to || [];
		let cc = prefill.cc || [];
		let bcc = prefill.bcc || [];
		let subject = prefill.subject || "";
		let html = prefill.html || "";
		let threadId = prefill.threadId || (source && source.threadId) || null;
		let inReplyTo = prefill.inReplyTo || (source && source.messageIdHeader) || null;
		let references = prefill.references || "";

		if (source && mode === "reply") {
			const { email } = splitEmail(source.from);
			if (to.length === 0 && email) to = [email];
			if (!subject) {
				subject = /^re:\s/i.test(source.subject || "") ? source.subject : `Re: ${source.subject || ""}`;
			}
			if (!html) html = buildQuotedBlock(source);
			if (!references) {
				const prev = source.references ? source.references + " " : "";
				references = (prev + (source.messageIdHeader || "")).trim();
			}
		}

		if (source && mode === "forward") {
			if (!subject) {
				subject = /^fwd:\s/i.test(source.subject || "") ? source.subject : `Fwd: ${source.subject || ""}`;
			}
			if (!html) html = buildForwardBlock(source);
			// Forward starts a new thread by default — don't carry threadId/inReplyTo
			threadId = null;
			inReplyTo = null;
			references = "";
		}

		return { mode, to, cc, bcc, subject, html, threadId, inReplyTo, references };
	}

	componentWillUnmount() {
		clearTimeout(this.autosaveTimer);
	}

	scheduleAutosave = () => {
		clearTimeout(this.autosaveTimer);
		this.autosaveTimer = setTimeout(this.autosave, AUTOSAVE_DELAY_MS);
	};

	autosave = async () => {
		const { to, cc, bcc, subject, html, threadId, draftId } = this.state;
		const isEmpty = to.length === 0 && cc.length === 0 && bcc.length === 0 && !subject && (!html || html === "<p><br></p>");
		if (isEmpty) return;

		const { accountId } = this.props;
		this.setState({ saving: true });
		const payload = { accountId, to, cc, bcc, subject, html, threadId };
		const result = draftId
			? await GmailService.updateDraft({ ...payload, draftId })
			: await GmailService.createDraft(payload);
		if (result && result.id) {
			this.setState({ draftId: result.id, saving: false, savedAt: new Date() });
		} else {
			this.setState({ saving: false });
		}
	};

	onFieldChange = (field, value) => {
		this.setState({ [field]: value }, this.scheduleAutosave);
	};

	onBodyChange = html => {
		this.setState({ html }, this.scheduleAutosave);
	};

	onPickFiles = e => {
		const incoming = Array.from(e.target.files || []);
		if (incoming.length === 0) return;
		this.setState(prev => ({ files: prev.files.concat(incoming) }));
		// allow re-picking the same file
		e.target.value = "";
	};

	onDropFiles = e => {
		e.preventDefault();
		e.stopPropagation();
		const incoming = Array.from(e.dataTransfer.files || []);
		if (incoming.length === 0) return;
		this.setState(prev => ({ files: prev.files.concat(incoming) }));
	};

	onDragOver = e => {
		e.preventDefault();
	};

	removeFile = idx => {
		this.setState(prev => ({ files: prev.files.filter((_, i) => i !== idx) }));
	};

	onSend = async () => {
		const { accountId, onSent } = this.props;
		const { to, cc, bcc, subject, html, threadId, inReplyTo, references, mode, draftId, files } = this.state;

		if (to.length === 0) {
			this.setState({ error: "Add at least one recipient" });
			return;
		}

		this.setState({ sending: true, error: null });

		const payload = { accountId, to, cc, bcc, subject, html, threadId, inReplyTo, references, mode, files };

		const result = draftId
			? await GmailService.sendDraft({ ...payload, draftId })
			: await GmailService.sendMessage(payload);

		this.setState({ sending: false });
		if (!result) {
			this.setState({ error: "Failed to send. Please retry." });
			return;
		}
		if (onSent) onSent(result, { mode, threadId: result.threadId });
	};

	onClose = async () => {
		// On close, fire any pending autosave so the draft is persisted before unmount.
		clearTimeout(this.autosaveTimer);
		await this.autosave();
		if (this.props.onClose) this.props.onClose();
	};

	onDiscard = async () => {
		if (!window.confirm("Discard this draft?")) return;
		const { accountId } = this.props;
		const { draftId } = this.state;
		if (draftId) await GmailService.deleteDraft({ accountId, draftId });
		if (this.props.onClose) this.props.onClose();
	};

	render() {
		const { to, cc, bcc, subject, html, files, sending, saving, savedAt, error, showCc, showBcc, mode } = this.state;
		const { accountEmail } = this.props;

		const title = mode === "reply" ? "Reply" : mode === "forward" ? "Forward" : "New message";

		const savedHint = sending
			? "Sending…"
			: saving
				? "Saving draft…"
				: savedAt
					? `Saved ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
					: "";

		return (
			<div className="compose-overlay" onClick={this.onClose}>
				<div className="compose-modal" onClick={e => e.stopPropagation()} onDragOver={this.onDragOver} onDrop={this.onDropFiles}>
					<div className="compose-modal__header">
						<div className="compose-modal__header-left">
							<Icon.Edit3 size={16} />
							<span>{title}</span>
							{accountEmail && <span className="compose-modal__from">from {accountEmail}</span>}
						</div>
						<div className="compose-modal__header-right">
							<span className="compose-modal__save-hint">{savedHint}</span>
							<button className="compose-modal__icon-btn" onClick={this.onClose} title="Close (saves draft)">
								<Icon.Minimize2 size={16} />
							</button>
						</div>
					</div>

					<div className="compose-modal__form">
						<RecipientField
							label="To"
							recipients={to}
							onChange={v => this.onFieldChange("to", v)}
							placeholder="Recipients"
							rightSlot={
								<div className="compose-modal__cc-toggles">
									{!showCc && (
										<button type="button" onClick={() => this.setState({ showCc: true })}>
											Cc
										</button>
									)}
									{!showBcc && (
										<button type="button" onClick={() => this.setState({ showBcc: true })}>
											Bcc
										</button>
									)}
								</div>
							}
						/>

						{showCc && (
							<RecipientField label="Cc" recipients={cc} onChange={v => this.onFieldChange("cc", v)} placeholder="" />
						)}
						{showBcc && (
							<RecipientField label="Bcc" recipients={bcc} onChange={v => this.onFieldChange("bcc", v)} placeholder="" />
						)}

						<div className="recipient-row recipient-row--subject">
							<input
								className="compose-modal__subject"
								type="text"
								placeholder="Subject"
								value={subject}
								onChange={e => this.onFieldChange("subject", e.target.value)}
							/>
						</div>

						<div className="compose-modal__editor">
							<ReactQuill
								theme="snow"
								value={html}
								onChange={this.onBodyChange}
								modules={QUILL_MODULES}
								formats={QUILL_FORMATS}
								placeholder="Compose your message…"
							/>
						</div>

						{files.length > 0 && (
							<div className="compose-modal__files">
								{files.map((f, i) => (
									<div key={i} className="compose-modal__file">
										<Icon.Paperclip size={14} />
										<span className="compose-modal__file-name" title={f.name}>
											{f.name}
										</span>
										<span className="compose-modal__file-size">{formatBytes(f.size)}</span>
										<button type="button" onClick={() => this.removeFile(i)} title="Remove">
											<Icon.X size={12} />
										</button>
									</div>
								))}
							</div>
						)}

						{error && (
							<div className="compose-modal__error">
								<Icon.AlertCircle size={14} /> {error}
							</div>
						)}
					</div>

					<div className="compose-modal__footer">
						<div className="compose-modal__footer-left">
							<button className="compose-modal__send" onClick={this.onSend} disabled={sending}>
								<Icon.Send size={14} /> {sending ? "Sending…" : "Send"}
							</button>
							<input
								ref={this.fileInputRef}
								type="file"
								multiple
								onChange={this.onPickFiles}
								style={{ display: "none" }}
							/>
							<button
								className="compose-modal__icon-btn compose-modal__icon-btn--soft"
								onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}
								title="Attach files"
							>
								<Icon.Paperclip size={16} />
							</button>
						</div>
						<div className="compose-modal__footer-right">
							<button className="compose-modal__icon-btn compose-modal__icon-btn--soft" onClick={this.onDiscard} title="Discard draft">
								<Icon.Trash2 size={16} />
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

export default ComposeModal;
